import { Injectable, signal } from '@angular/core';
import { Location } from '@angular/common';
import { Observable, Subject } from 'rxjs';
import { TwoFactorGateReason } from '../../enums/two-factor-gate-reason.enum';

/**
 * Asks the two-factor enrollment gate for callers with no template of their own: `SuperUser2faGuard`
 * on the way into the app, `FinancialConnectionsService` before KYC, the launchpad card and the v2
 * Treasury shell. `app-two-factor-gate-dialog-host` at the app root renders the dialog bound to
 * `open`.
 *
 * `ask()` resolves `true` only once the user has enrolled; every other way out resolves `false`.
 * Kept free of component imports: the guard injects it, and the dialog imports the guard's caller.
 */
@Injectable({ providedIn: 'root' })
export class TwoFactorGateDialogStateService {
    readonly open = signal(false);
    readonly reason = signal<TwoFactorGateReason | undefined>(undefined);

    private _result$: Subject<boolean> | null = null;

    constructor(location: Location) {
        // Browser back/forward cancels, as MatDialog's closeOnNavigation did (even with disableClose).
        location.subscribe(() => this.answer(false));
    }

    ask(reason?: TwoFactorGateReason): Observable<boolean> {
        this.answer(false);
        this._result$ = new Subject<boolean>();
        this.reason.set(reason);
        this.open.set(true);

        return this._result$.asObservable();
    }

    answer(enrolled: boolean): void {
        const result$ = this._result$;
        this._result$ = null;
        this.open.set(false);
        result$?.next(enrolled);
        result$?.complete();
    }
}
